import type { QuoteStatus } from "../../convex/schemas/validators/quoteEnums.lib";
import type {
  OrderPaymentStatus,
  OrderStage,
} from "../../convex/constants/statuses.lib";
import { STAGE, type Stage } from "../../convex/constants/stage.lib";
import { resolveOrderStage, resolveQuoteStage } from "./stage-resolver";
import { getStageLabel } from "../../convex/stage/projections.lib";

export type DealLifecycleStage = Stage;
export type DealLifecycleAxis =
  | "pre_quote"
  | "quote_open"
  | "quote_terminal"
  | "order_active"
  | "order_terminal";

export interface DealLifecycleQuoteInput {
  status: QuoteStatus;
  quoteSource?: "buyer" | "vendor" | "admin" | "vendor_direct";
  expiresAt?: number | null;
  recycleOfferType?: string | null;
}

export interface DealLifecycleOrderInput {
  stage: OrderStage;
  resolvedStage?: Stage;
  isPickup?: boolean;
  paymentStatus?: OrderPaymentStatus;
  scheduling?: Parameters<typeof resolveOrderStage>[0]["scheduling"];
  pricing?: Parameters<typeof resolveOrderStage>[0]["pricing"];
  payment?: Parameters<typeof resolveOrderStage>[0]["payment"];
}

export interface DealLifecycleInput {
  quote: DealLifecycleQuoteInput;
  order?: DealLifecycleOrderInput;
  now?: number;
}

export interface DealLifecycleState {
  stage: Stage;
  exceptionKind?: "rejected" | "no_response";
  axis: DealLifecycleAxis;
  progress: number;
  isTerminal: boolean;
  isException: boolean;
  isPickup?: boolean;
}

const HAPPY_PATH: readonly Stage[] = [
  STAGE.DRAFT,
  STAGE.SUBMITTED,
  STAGE.VENDOR_RESPONDING,
  STAGE.AWAITING_REVIEW,
  STAGE.CONFIRMING_PICKUP,
  STAGE.CONFIRMED_AWAITING_PAYMENT,
  STAGE.AWAITING_SCHEDULING,
  STAGE.SCHEDULED,
  STAGE.IN_TRANSIT,
  STAGE.DELIVERED,
  STAGE.COMPLETE,
];

const TERMINAL_STAGES: ReadonlySet<Stage> = new Set([
  STAGE.COMPLETE,
  STAGE.CANCELLED,
  STAGE.REFUNDED,
  STAGE.EXPIRED,
]);

const EXCEPTION_STAGES: ReadonlySet<Stage> = new Set([
  STAGE.CANCELLED,
  STAGE.REFUNDED,
  STAGE.EXPIRED,
]);

export function resolveDealLifecycle(
  input: DealLifecycleInput,
): DealLifecycleState {
  const stage = input.order
    ? (input.order.resolvedStage ??
      resolveOrderStage({
        ...input.order,
        paymentStatus: input.order.paymentStatus ?? "paid",
      }).stage)
    : resolveQuoteStage({ ...input.quote, now: input.now }).stage;
  const quoteStatus = input.quote.status;
  const exceptionKind =
    !input.order &&
    (quoteStatus === "rejected" || quoteStatus === "no_response")
      ? quoteStatus
      : undefined;
  return {
    stage,
    axis: stageAxis(stage),
    progress: EXCEPTION_STAGES.has(stage) ? -1 : HAPPY_PATH.indexOf(stage),
    isTerminal: TERMINAL_STAGES.has(stage),
    isException: EXCEPTION_STAGES.has(stage),
    ...(exceptionKind ? { exceptionKind } : {}),
    ...(input.order?.isPickup ? { isPickup: true } : {}),
  };
}

function stageAxis(stage: Stage): DealLifecycleAxis {
  if (
    stage === STAGE.DRAFT ||
    stage === STAGE.SUBMITTED ||
    stage === STAGE.VENDOR_RESPONDING
  ) {
    return "pre_quote";
  }
  if (stage === STAGE.AWAITING_REVIEW) return "quote_open";
  if (stage === STAGE.EXPIRED) return "quote_terminal";
  if (
    stage === STAGE.CONFIRMING_PICKUP ||
    stage === STAGE.CONFIRMED_AWAITING_PAYMENT ||
    stage === STAGE.AWAITING_SCHEDULING ||
    stage === STAGE.SCHEDULED ||
    stage === STAGE.IN_TRANSIT ||
    stage === STAGE.DELIVERED
  ) {
    return "order_active";
  }
  return "order_terminal";
}

export function getDealLifecycleHappyPathLength(): number {
  return HAPPY_PATH.length;
}

export function getAllDealLifecycleStages(): readonly Stage[] {
  return [...HAPPY_PATH, STAGE.CANCELLED, STAGE.REFUNDED, STAGE.EXPIRED];
}

export type DealPipelineStep =
  | "drafted"
  | "sent"
  | "awaiting_response"
  | "scheduling"
  | "in_transit"
  | "delivered";

export const DEAL_PIPELINE_STEPS: readonly DealPipelineStep[] = [
  "drafted",
  "sent",
  "awaiting_response",
  "scheduling",
  "in_transit",
  "delivered",
];

const STAGE_TO_PIPELINE: Record<Stage, DealPipelineStep> = {
  draft: "drafted",
  submitted: "drafted",
  vendor_responding: "sent",
  awaiting_review: "awaiting_response",
  confirming_pickup: "scheduling",
  confirmed_awaiting_payment: "awaiting_response",
  awaiting_scheduling: "scheduling",
  scheduled: "scheduling",
  in_transit: "in_transit",
  delivered: "delivered",
  complete: "delivered",
  cancelled: "awaiting_response",
  refunded: "delivered",
  expired: "awaiting_response",
};

const PIPELINE_LABELS: Record<DealPipelineStep, string> = {
  drafted: "Drafted",
  sent: "Sent",
  awaiting_response: "Awaiting response",
  scheduling: "Scheduling",
  in_transit: "In transit",
  delivered: "Delivered",
};

export function getPipelineStep(state: DealLifecycleState): DealPipelineStep {
  return STAGE_TO_PIPELINE[state.stage];
}

export function pipelineStepLabel(
  step: DealPipelineStep,
  state?: Pick<DealLifecycleState, "isPickup">,
): string {
  if (state?.isPickup && step === "in_transit") return "Picked up";
  return PIPELINE_LABELS[step];
}

export function getPipelineStepIndex(step: DealPipelineStep): number {
  return DEAL_PIPELINE_STEPS.indexOf(step);
}

export type DealLifecycleViewer = "buyer" | "vendor" | "admin";

export function getDealStageHeadline(
  stage: Stage,
  viewer: DealLifecycleViewer,
  quoteSource?: DealLifecycleQuoteInput["quoteSource"],
  exceptionKind?: DealLifecycleState["exceptionKind"],
): string {
  if (stage === STAGE.AWAITING_REVIEW) {
    if (quoteSource === "buyer") {
      if (viewer === "buyer") return "You sent an offer — awaiting seller";
      if (viewer === "vendor") return "You have an open offer";
      return "Buyer offer is open and unanswered";
    }
    if (viewer === "buyer") return "You have an open offer";
    if (viewer === "vendor") return "You sent an offer — awaiting buyer";
    return "Offer is open and unanswered";
  }
  if (stage === STAGE.CONFIRMED_AWAITING_PAYMENT) {
    return "Payment or credit approval is pending";
  }
  if (stage === STAGE.CONFIRMING_PICKUP) {
    return "Pickup confirmation is pending";
  }
  if (stage === STAGE.AWAITING_SCHEDULING) {
    return "Order confirmed — pickup scheduled next";
  }
  if (stage === STAGE.SCHEDULED) return "Pickup date confirmed by both sides";
  if (stage === STAGE.IN_TRANSIT) return "Order is in transit";
  if (stage === STAGE.DELIVERED) return "Order delivered";
  if (stage === STAGE.COMPLETE) return "Deal complete";
  if (stage === STAGE.CANCELLED) {
    return exceptionKind === "rejected"
      ? "Offer was declined"
      : "Deal was cancelled";
  }
  if (stage === STAGE.REFUNDED) return "Order was refunded";
  if (stage === STAGE.EXPIRED) {
    return exceptionKind === "no_response"
      ? "No response on this quote"
      : "Offer expired before either side acted";
  }
  return getStageLabel(stage);
}
